import * as React from 'react';

import { ContainerStatus } from '@rematter/frontend-lib-api-client';
import { cn, Row, Select } from '@rematter/ui-web';

import { EntitySelectProps } from './types';

import { ContainerStatusBadge } from '~/shared/components/ContainerStatusBadge';
import { useTranslations } from '~/shared/i18n/useTranslations';
import { createQueryHook } from '~/shared/providers/api';

export type AvailableContainer = {
  id: string;
  containerNumber: string | null;
  status: ContainerStatus;
  containerTypeId: string;
  containerType: {
    id: string;
    name: string;
  } | null;
  loadId: string | null;
};

export const useAvailableContainersQuery = createQueryHook(
  'shared.availableContainers',
  ({
    bookingId,
    containerTypeId,
  }: {
    bookingId: string;
    containerTypeId?: string;
  }) => ({
    containers: {
      __args: {
        filter: {
          bookingIds: [bookingId],
          containerTypeIds: containerTypeId ? [containerTypeId] : undefined,
          status: ['AtFacilityEmpty', 'AtFacilityLoaded'],
        },
        sorting: ['ContainerNumberAsc'],
        pagination: {
          limit: 100,
          offset: 0,
        },
      },
      entries: {
        id: true,
        containerNumber: true,
        status: true,
        containerTypeId: true,
        containerType: {
          id: true,
          name: true,
        },
        loadId: true,
      },
      page: {
        total: true,
      },
    },
  }),
  {
    select: (data): AvailableContainer[] => {
      if (!data?.containers?.entries) {
        return [];
      }
      return data.containers.entries.map((container) => ({
        id: container.id,
        containerNumber: container.containerNumber || null,
        status: container.status as ContainerStatus,
        containerTypeId: container.containerTypeId,
        containerType: container.containerType || null,
        loadId: container.loadId || null,
      }));
    },
  },
);

export const SelectContainerInstance = ({
  onChange,
  value,
  bookingId,
  containerTypeId,
  currentLoadId,
  ...props
}: EntitySelectProps<AvailableContainer> & {
  bookingId?: string | null;
  containerTypeId?: string | null;
  currentLoadId?: string | null;
}) => {
  const t = useTranslations();

  const {
    data: allContainers,
    isLoading,
    isFetched,
  } = useAvailableContainersQuery({
    bookingId: bookingId || '',
    containerTypeId: containerTypeId || undefined,
    queryOptions: {
      enabled: !!bookingId,
    },
  });

  const containers = React.useMemo(() => {
    if (!allContainers) return [];
    return allContainers.filter((c) => !c.loadId || c.loadId === currentLoadId);
  }, [allContainers, currentLoadId]);

  const options = React.useMemo(
    () =>
      containers.map((container) => {
        const containerLabel = container.containerNumber
          ? `${container.containerNumber} (${container.containerType?.name ?? t.selectFields.containerInstance.unknownType})`
          : `${t.selectFields.containerInstance.unassignedContainer} (${container.containerType?.name ?? t.selectFields.containerInstance.unknownType})`;

        return {
          key: container.id,
          label: (
            <Row xBetween yCenter gap1>
              <span className="truncate">{containerLabel}</span>
              <ContainerStatusBadge status={container.status} />
            </Row>
          ),
          selectedLabel: containerLabel,
          searchable: containerLabel,
        };
      }),
    [containers, t],
  );

  const handleChange = React.useCallback(
    (selectedValue: string | undefined) => {
      if (!selectedValue) {
        onChange?.(undefined, undefined as never);
        return;
      }
      const selectedContainer = containers?.find((c) => c.id === selectedValue);

      if (selectedContainer) {
        onChange?.(selectedValue, selectedContainer);
      }
    },
    [containers, onChange],
  );

  const noContainersAvailable =
    !!bookingId && isFetched && containers.length === 0;

  return (
    <Select
      {...props}
      value={value || undefined}
      onChange={handleChange}
      options={options}
      isLoading={isLoading}
      isDisabled={props.isDisabled || !bookingId}
      placeholder={
        noContainersAvailable
          ? t.selectFields.containerInstance.noContainersAvailable
          : props.placeholder
      }
      className={cn('@SelectContainerInstance', props.className)}
    />
  );
};
